import { APP_ROUTES } from '../../navigation/appRoutes';

/**
 * Static catalog of product tips / “what’s new” entries.
 * Append a new tip here for future features — no UI rewrite required.
 */

export type FeatureTip = {
  /** Stable id used for dismiss persistence (never rename once shipped). */
  id: string;
  title: string;
  summary: string;
  benefits: string[];
  howTo?: string[];
  /** Optional route where the feature lives (e.g. Tasks page). */
  routeHint?: string;
  /** ISO date for sorting / “New” badge. */
  releasedAt?: string;
};

export const FEATURE_TIPS: FeatureTip[] = [
  {
    id: 'look-ahead',
    title: 'Look ahead',
    summary:
      'Focus the schedule on near-term work so the full WBS doesn’t get in the way.',
    benefits: [
      'Prep upcoming work — see the next set of tasks so planners and site engineers can arrange materials, labour, and other resources.',
      'Spot delay impact — understand which upcoming tasks are at risk if current work slips, and get a clearer sense of near-term timing.',
    ],
    howTo: [
      'Switch Schedule → Look ahead on the Tasks page.',
      'By default you see tasks in the look-ahead window (based on Scale) plus their WBS parents.',
      'Use Show full WBS when you need the whole tree again.',
    ],
    routeHint: APP_ROUTES.schedule.tasks,
    releasedAt: '2026-08-06',
  },
  {
    id: 'schedule-view',
    title: 'Schedule view',
    summary:
      'Plan and adjust the full project timeline — dates, links, duration, and progress — in one Gantt workspace.',
    benefits: [
      'See the whole WBS with the timeline on the right so sequencing and critical path stay visible while you edit.',
      'Make schedule changes locally, then Save to Server when ready — or Discard to reload the last saved plan.',
    ],
    howTo: [
      'Open Tasks and stay on (or switch to) Schedule.',
      'Edit tasks in the grid or by dragging bars; use Live Schedule / Recalculate when you need dependency updates.',
      'Use Scale, Columns, and Tools (View / Export) for how the chart is shown; Export when you need a file out.',
    ],
    routeHint: APP_ROUTES.schedule.tasks,
    releasedAt: '2026-08-06',
  },
  {
    id: 'import-planning',
    title: 'Project setup',
    summary:
      'Choose how to begin: import a plan, start blank on the Gantt, or build locations first.',
    benefits: [
      'Import when you already have MPP, MSP, P6, or XML — then review the proposed locations.',
      'Or skip upload: open Tasks with a blank schedule, or set up the site hierarchy first and assign later.',
    ],
    howTo: [
      'Open Project setup under Schedule.',
      'Pick Import, Start blank, or Build locations first.',
      'If you import, continue to locations and Finish linking when ready — edit manually if the AI proposal is thin.',
    ],
    routeHint: APP_ROUTES.schedule.import,
    releasedAt: '2026-09-15',
  },
  {
    id: 'locations-and-tasks',
    title: 'Locations and tasks',
    summary:
      'Locations describe where work happens; leaf tasks should carry a location so planners and site teams stay aligned.',
    benefits: [
      'Assign on the Location name column or Task Details so each leaf task has a place on site.',
      'Build the hierarchy in Location management first if you need blocks and floors before scheduling.',
      'Locations tell the schedule where work happens so site reporting in Handoff stays accurate.',
    ],
    howTo: [
      'Watch Schedule readiness on Tasks and Location management until leaf tasks are covered.',
      'Use Assign location in Task Details, or Reassign locations from Tools → Tasks after the tree is ready.',
    ],
    routeHint: APP_ROUTES.schedule.tasks,
    releasedAt: '2026-09-15',
  },
  {
    id: 'location-management',
    title: 'Location management',
    summary:
      'Build and maintain the project location hierarchy that tasks map to — blocks, floors, and other site levels.',
    benefits: [
      'Keep a clear site structure so planners know where work happens and reports stay location-aware.',
      'Edit, reorganize, and classify locations (including AI-assisted options) without leaving the scheduling app.',
    ],
    howTo: [
      'Open Location Management under Schedule.',
      'Use the tree to add, rename, move, or remove locations; select a node to edit details on the right.',
      'Save drafts when you are ready so location changes stick for the project.',
    ],
    routeHint: APP_ROUTES.schedule.locations,
    releasedAt: '2026-08-06',
  },
  {
    id: 'calendar',
    title: 'Calendar',
    summary:
      'Define working time for the project — week rules, exceptions, and day overrides that drive schedule dates.',
    benefits: [
      'Reflect real site calendars (weekends, holidays, special working days) so durations and finishes stay realistic.',
      'Manage multiple calendars and assign the right working pattern for how planning should behave.',
    ],
    howTo: [
      'Open Calendar under Schedule and select or create a calendar.',
      'Set overview details, then adjust week rules, exceptions, and day overrides in the tabs.',
      'Refresh or switch calendars anytime you need to review another working pattern.',
    ],
    routeHint: APP_ROUTES.schedule.calendar,
    releasedAt: '2026-08-06',
  },
];

export function getFeatureTipById(id: string): FeatureTip | undefined {
  return FEATURE_TIPS.find((tip) => tip.id === id);
}

/** Newest first when releasedAt is set. */
export function getFeatureTipsSorted(): FeatureTip[] {
  return [...FEATURE_TIPS].sort((a, b) => {
    const aTime = a.releasedAt ? Date.parse(a.releasedAt) : 0;
    const bTime = b.releasedAt ? Date.parse(b.releasedAt) : 0;
    return bTime - aTime;
  });
}
